/**
 * Builds an optimised URL for event listing images.
 *
 * For proxy endpoints (/api/application/event/{id}/image) the function appends
 * a ?w= parameter so the backend can resize to the requested pixel width before
 * streaming, dramatically reducing transferred payload for thumbnail-sized cards.
 *
 * Direct blob/SAS URLs are returned unchanged because they cannot be resized
 * without a CDN or additional proxy layer.
 */
export function buildEventImageUrl(imageUrl: string | null | undefined, width: number): string | null {
    if (!imageUrl) return null;

    const trimmed = imageUrl.trim();
    if (!trimmed) return null;

    // Only append ?w= to our own API proxy routes.
    const isProxyRoute = /\/api\/application\/event\/[^/]+\/image/.test(trimmed);
    if (!isProxyRoute) return trimmed;

    try {
        // Preserve any existing query params (e.g. ?t= token).
        const separator = trimmed.includes('?') ? '&' : '?';
        return `${trimmed}${separator}w=${Math.round(width)}`;
    } catch {
        return trimmed;
    }
}

/**
 * Returns true when the given URL is an authenticated API proxy route that
 * requires a JavaScript blob-fetch (cannot be used directly in an <img> src).
 */
export function isProtectedEventImageRoute(imageUrl: string | null | undefined): boolean {
    if (!imageUrl) return false;
    return /\/api\/application\/event\/[^/]+\/image(?:\?.*)?$/.test(imageUrl.trim());
}
